/**
 * Shared weekly/monthly period-trend building blocks — comparison tables,
 * bar charts, and period-boundary helpers. Used by the site-wide
 * PerformanceOverview and the per-article ArticleDetailPanel.
 */

import React from 'react';

export function fmt(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 10000) return `${(n / 1000).toFixed(0)}k`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(Math.round(n));
}

export function pctDelta(current: number, previous: number): { text: string; color: string; arrow: string } {
  if (previous === 0) {
    if (current === 0) return { text: '—', color: 'text-gray-400', arrow: '' };
    return { text: 'new', color: 'text-green-600', arrow: '↑' };
  }
  const pct = ((current - previous) / previous) * 100;
  if (pct > 0) return { text: `${pct.toFixed(1)}%`, color: 'text-green-600', arrow: '↑' };
  if (pct < 0) return { text: `${Math.abs(pct).toFixed(1)}%`, color: 'text-red-500', arrow: '↓' };
  return { text: '0%', color: 'text-gray-400', arrow: '→' };
}

// Parse "YYYY-MM-DD" as a LOCAL date (new Date(iso) would parse as UTC
// midnight and shift a day in negative-offset timezones)
export function parseIsoDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function fmtShortDate(d: Date): string {
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export type PeriodDate = { start: Date; end: Date; label: string };

// Build week periods from server-provided ISO Monday dates (single source of
// truth — aligned with the backend's series buckets)
export function weekDatesFromStarts(starts: string[]): PeriodDate[] {
  return starts.map(s => {
    const start = parseIsoDate(s);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return { start, end, label: `Wk ${fmtShortDate(start)}` };
  });
}

// Build month periods from server-provided ISO first-of-month dates
export function monthDatesFromStarts(starts: string[]): PeriodDate[] {
  return starts.map(s => {
    const start = parseIsoDate(s);
    const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
    return { start, end, label: start.toLocaleDateString(undefined, { month: 'short' }) };
  });
}

// Compute ISO week dates (Mon–Sun) for each weekly bar
// (fallback when the server hasn't provided weekly_period_starts yet)
export function weekDates(numWeeks: number): PeriodDate[] {
  const today = new Date();
  // Find Monday of current ISO week
  const day = today.getDay(); // 0=Sun
  const mondayOffset = day === 0 ? -6 : 1 - day;
  const thisMonday = new Date(today);
  thisMonday.setDate(today.getDate() + mondayOffset);

  const dates: PeriodDate[] = [];
  for (let w = numWeeks - 1; w >= 0; w--) {
    const start = new Date(thisMonday);
    start.setDate(thisMonday.getDate() - w * 7);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    const label = `Wk ${fmtShortDate(start)}`;
    dates.push({ start, end, label });
  }
  return dates;
}

// Compute calendar month boundaries for the monthly bars
export function calendarMonthDates(numMonths: number): PeriodDate[] {
  const today = new Date();
  const dates: PeriodDate[] = [];
  for (let m = numMonths - 1; m >= 0; m--) {
    const totalMonths = today.getFullYear() * 12 + today.getMonth() - m;
    const year = Math.floor(totalMonths / 12);
    const month = totalMonths % 12; // 0-indexed
    const start = new Date(year, month, 1);
    const end = new Date(year, month + 1, 0); // last day of month
    const label = start.toLocaleDateString(undefined, { month: 'short' });
    dates.push({ start, end, label });
  }
  return dates;
}

// Period comparison table — compares the last two COMPLETE periods. The
// in-progress period (last series entry) is shown as a de-emphasized footnote
// so a 2-day week is never compared against a full one.
export function PeriodComparisonTable({ title, currentLabel, currentSub, previousLabel, previousSub, metrics, inProgressNote }: {
  title: string;
  currentLabel: string;
  currentSub: string;
  previousLabel: string;
  previousSub: string;
  metrics: { label: string; current: number; previous: number; inProgress: number }[];
  inProgressNote: string;
}) {
  return (
    <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
      <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide px-4 pt-3 pb-2">{title}</div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] text-gray-400 uppercase border-b border-gray-100">
            <th className="text-left px-4 py-1.5 font-medium">Metric</th>
            <th className="text-right px-4 py-1.5 font-medium">
              {previousLabel}
              <span className="block normal-case font-normal text-gray-500">{previousSub}</span>
            </th>
            <th className="text-right px-4 py-1.5 font-medium">
              {currentLabel}
              <span className="block normal-case font-normal text-gray-500">{currentSub}</span>
            </th>
            <th className="text-right px-4 py-1.5 font-medium">Delta</th>
            <th className="text-center px-4 py-1.5 font-medium">Trend</th>
          </tr>
        </thead>
        <tbody>
          {metrics.map((m) => {
            const delta = pctDelta(m.current, m.previous);
            return (
              <tr key={m.label} className="border-b border-gray-50">
                <td className="px-4 py-2 text-gray-700">{m.label}</td>
                <td className="px-4 py-2 text-right text-gray-500 tabular-nums">{fmt(m.previous)}</td>
                <td className="px-4 py-2 text-right font-medium tabular-nums">{fmt(m.current)}</td>
                <td className={`px-4 py-2 text-right font-medium ${delta.color}`}>{delta.arrow} {delta.text}</td>
                <td className="px-4 py-2 text-center">
                  <span className={`text-lg ${delta.color}`}>{delta.arrow}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="px-4 py-2 text-[11px] text-gray-400">
        {inProgressNote}: {metrics.map(m => `${fmt(m.inProgress)} ${m.label.toLowerCase()}`).join(' · ')}
      </div>
    </div>
  );
}

// Generic period bar chart — works for weekly, monthly, or any period
export function PeriodBarChart({ values, color, label, periodLabel, dates }: {
  values: number[];
  color: string;
  label: string;
  periodLabel: string; // e.g. "last week" or "last month"
  dates?: PeriodDate[];
}) {
  if (!values.length) return null;

  // Use last COMPLETE period for the headline (current period is partial)
  const lastCompleteVal = values[values.length - 2] || 0;
  const prevCompleteVal = values[values.length - 3] || 0;
  const delta = prevCompleteVal > 0 ? ((lastCompleteVal - prevCompleteVal) / prevCompleteVal * 100) : 0;

  // Exclude the current partial period from the max so it doesn't shrink the scale
  const completeValues = values.slice(0, -1);
  const maxVal = Math.max(...completeValues, 1);
  const barMaxH = 80;

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white">
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">{label}</div>
        <div className="text-right">
          <span className="text-sm font-bold text-gray-900 tabular-nums">{fmt(lastCompleteVal)}</span>
          <span className="text-[10px] text-gray-400 ml-1">{periodLabel}</span>
          {delta !== 0 && (
            <span className={`text-[10px] ml-2 ${delta > 0 ? 'text-green-600' : 'text-red-500'}`}>
              {delta > 0 ? '↑' : '↓'}{Math.abs(delta).toFixed(0)}%
            </span>
          )}
        </div>
      </div>
      <div className="flex items-end gap-px" style={{ height: barMaxH }}>
        {values.map((val, i) => {
          const isPartial = i === values.length - 1;
          const barH = Math.max((val / maxVal) * barMaxH, 1);
          const d = dates?.[i];
          const tooltip = d
            ? `${d.label} (${fmtShortDate(d.start)} – ${fmtShortDate(d.end)}): ${val.toLocaleString()}${isPartial ? ' (partial)' : ''}`
            : `${val.toLocaleString()}`;
          return (
            <div
              key={i}
              className="flex-1 rounded-t"
              style={{
                height: barH,
                backgroundColor: color,
                opacity: isPartial ? 0.4 : 1,
              }}
              title={tooltip}
            />
          );
        })}
      </div>
      <div className="flex justify-between mt-1">
        <span className="text-[9px] text-gray-400">{dates?.[0]?.label ?? ''}</span>
        <span className="text-[9px] text-gray-400">{dates?.[dates.length - 1]?.label ?? 'Now'}</span>
      </div>
    </div>
  );
}
